/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  OemAssetStatus,
  OemPlanChangeRequestStatus,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface OemPlanChangeRequestProps {
  id: string;
  oemPlanId: string;
  oemAssetId: string;
  status: OemPlanChangeRequestStatus;
  originalAssetStatus: OemAssetStatus;
  requestedAssetStatus: OemAssetStatus;
  requestedById: string | null;
  dateRequested: Date | null;
  dateCompleted: Date | null;
  dateCancelled: Date | null;
  notes: string | null;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class OemPlanChangeRequestRecord extends Record<OemPlanChangeRequestProps>({
  id: null as any,
  oemPlanId: null as any,
  oemAssetId: null as any,
  status: null as any,
  originalAssetStatus: null as any,
  requestedAssetStatus: null as any,
  requestedById: null as any,
  dateRequested: null as any,
  dateCompleted: null as any,
  dateCancelled: null as any,
  notes: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    oemPlanId: string;
    oemAssetId: string;
    status: OemPlanChangeRequestStatus;
    originalAssetStatus: OemAssetStatus;
    requestedAssetStatus: OemAssetStatus;
    requestedById: string | null;
    dateRequested: string | number | Date | null;
    dateCompleted: string | number | Date | null;
    dateCancelled: string | number | Date | null;
    notes: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): OemPlanChangeRequestRecord {
    if (json == null) {
      throw new Error('OemPlanChangeRequestRecord.fromJS: parameter cannot be null');
    }
    return new OemPlanChangeRequestRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'OemPlanChangeRequestJson.id',
      ),
      oemPlanId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'oemPlanId', '00000000-0000-0000-0000-000000000000'),
        'OemPlanChangeRequestJson.oemPlanId',
      ),
      oemAssetId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'oemAssetId', '00000000-0000-0000-0000-000000000000'),
        'OemPlanChangeRequestJson.oemAssetId',
      ),
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', OemPlanChangeRequestStatus.Draft),
        'OemPlanChangeRequestJson.status',
      ),
      originalAssetStatus: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'originalAssetStatus', OemAssetStatus.Standby),
        'OemPlanChangeRequestJson.originalAssetStatus',
      ),
      requestedAssetStatus: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requestedAssetStatus', OemAssetStatus.Standby),
        'OemPlanChangeRequestJson.requestedAssetStatus',
      ),
      requestedById: json.requestedById,
      dateRequested: json.dateRequested != null ? new Date(json.dateRequested) : null,
      dateCompleted: json.dateCompleted != null ? new Date(json.dateCompleted) : null,
      dateCancelled: json.dateCancelled != null ? new Date(json.dateCancelled) : null,
      notes: json.notes,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    oemPlanId: string;
    oemAssetId: string;
    status: OemPlanChangeRequestStatus;
    originalAssetStatus: OemAssetStatus;
    requestedAssetStatus: OemAssetStatus;
    requestedById: string | null;
    dateRequested: string | number | Date | null;
    dateCompleted: string | number | Date | null;
    dateCancelled: string | number | Date | null;
    notes: string | null;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [OemPlanChangeRequestRecord, Error[]] {
    if (json == null) {
      throw new Error('OemPlanChangeRequestRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new OemPlanChangeRequestRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'OemPlanChangeRequestJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oemPlanId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'oemPlanId', '00000000-0000-0000-0000-000000000000'),
            'OemPlanChangeRequestJson.oemPlanId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      oemAssetId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'oemAssetId', '00000000-0000-0000-0000-000000000000'),
            'OemPlanChangeRequestJson.oemAssetId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', OemPlanChangeRequestStatus.Draft),
            'OemPlanChangeRequestJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      originalAssetStatus: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'originalAssetStatus', OemAssetStatus.Standby),
            'OemPlanChangeRequestJson.originalAssetStatus',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedAssetStatus: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'requestedAssetStatus', OemAssetStatus.Standby),
            'OemPlanChangeRequestJson.requestedAssetStatus',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requestedById: tryOrDefault(
        () => json.requestedById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateRequested: tryOrDefault(
        () => (json.dateRequested != null ? new Date(json.dateRequested) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateCompleted: tryOrDefault(
        () => (json.dateCompleted != null ? new Date(json.dateCompleted) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateCancelled: tryOrDefault(
        () => (json.dateCancelled != null ? new Date(json.dateCancelled) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: OemPlanChangeRequestProps) {
    super(props);
    if (props == null) {
      throw new Error('OemPlanChangeRequestRecord constructor parameter cannot be null');
    }
  }
}

export abstract class OemPlanChangeRequestViewModelBase implements OemPlanChangeRequestProps {
  protected readonly _props: OemPlanChangeRequestProps;
  private _json?: OemPlanChangeRequestProps;

  get id(): string {
    return this._props.id;
  }
  get oemPlanId(): string {
    return this._props.oemPlanId;
  }
  get oemAssetId(): string {
    return this._props.oemAssetId;
  }
  get status(): OemPlanChangeRequestStatus {
    return this._props.status;
  }
  get originalAssetStatus(): OemAssetStatus {
    return this._props.originalAssetStatus;
  }
  get requestedAssetStatus(): OemAssetStatus {
    return this._props.requestedAssetStatus;
  }
  get requestedById(): string | null {
    return this._props.requestedById;
  }
  get dateRequested(): Date | null {
    return this._props.dateRequested;
  }
  get dateCompleted(): Date | null {
    return this._props.dateCompleted;
  }
  get dateCancelled(): Date | null {
    return this._props.dateCancelled;
  }
  get notes(): string | null {
    return this._props.notes;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCancelled() {
    return this._props.status === OemPlanChangeRequestStatus.Cancelled;
  }
  public get isCompleted() {
    return this._props.status === OemPlanChangeRequestStatus.Completed;
  }
  public get isDraft() {
    return this._props.status === OemPlanChangeRequestStatus.Draft;
  }
  public get isRequested() {
    return this._props.status === OemPlanChangeRequestStatus.Requested;
  }
  public get isOriginalAssetStatusActivated() {
    return this._props.originalAssetStatus === OemAssetStatus.Activated;
  }
  public get isRequestedAssetStatusActivated() {
    return this._props.requestedAssetStatus === OemAssetStatus.Activated;
  }
  public get isOriginalAssetStatusDemobilized() {
    return this._props.originalAssetStatus === OemAssetStatus.Demobilized;
  }
  public get isRequestedAssetStatusDemobilized() {
    return this._props.requestedAssetStatus === OemAssetStatus.Demobilized;
  }
  public get isOriginalAssetStatusStandby() {
    return this._props.originalAssetStatus === OemAssetStatus.Standby;
  }
  public get isRequestedAssetStatusStandby() {
    return this._props.requestedAssetStatus === OemAssetStatus.Standby;
  }

  public isAnyStatus(...oemPlanChangeRequestStatus: OemPlanChangeRequestStatus[]) {
    return oemPlanChangeRequestStatus.includes(this._props.status);
  }
  public isAnyOriginalAssetStatus(...oemAssetStatus: OemAssetStatus[]) {
    return oemAssetStatus.includes(this._props.originalAssetStatus);
  }
  public isAnyRequestedAssetStatus(...oemAssetStatus: OemAssetStatus[]) {
    return oemAssetStatus.includes(this._props.requestedAssetStatus);
  }

  constructor(props: OemPlanChangeRequestProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        oemPlanId: this._props.oemPlanId,
        oemAssetId: this._props.oemAssetId,
        status: this._props.status,
        originalAssetStatus: this._props.originalAssetStatus,
        requestedAssetStatus: this._props.requestedAssetStatus,
        requestedById: this._props.requestedById,
        dateRequested: this._props.dateRequested,
        dateCompleted: this._props.dateCompleted,
        dateCancelled: this._props.dateCancelled,
        notes: this._props.notes,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllOemPlanChangeRequestEntities = (state: AppState) =>
  state.entityStores.oemPlanChangeRequest.entities;

export const selectOemPlanChangeRequestById = (id: string) => (state: AppState) =>
  state.entityStores.oemPlanChangeRequest.entities.get(id);

export const selectOemPlanChangeRequestMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.oemPlanChangeRequest.meta.get(id);

export const selectOemPlanChangeRequestAndMetadataById = (id: string) =>
  createSelector(
    selectOemPlanChangeRequestById(id),
    selectOemPlanChangeRequestMetadataById(id),
    (oemPlanChangeRequest, meta) => ({ oemPlanChangeRequest, meta }),
  );

export function selectOemPlanChangeRequestEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllOemPlanChangeRequestEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, OemPlanChangeRequestRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectOemPlanChangeRequestEntitiesByOemPlanId(oemPlanId: string) {
  return createSelector(selectAllOemPlanChangeRequestEntities, (entities) =>
    entities.filter((e) => e.oemPlanId === oemPlanId).valueSeq(),
  );
}

export function selectOemPlanChangeRequestEntitiesByOemAssetId(oemAssetId: string) {
  return createSelector(selectAllOemPlanChangeRequestEntities, (entities) =>
    entities.filter((e) => e.oemAssetId === oemAssetId).valueSeq(),
  );
}

export function selectOemPlanChangeRequestEntitiesByStatus(status: OemPlanChangeRequestStatus) {
  return createSelector(selectAllOemPlanChangeRequestEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectOemPlanChangeRequestEntitiesByOriginalAssetStatus(
  originalAssetStatus: OemAssetStatus,
) {
  return createSelector(selectAllOemPlanChangeRequestEntities, (entities) =>
    entities.filter((e) => e.originalAssetStatus === originalAssetStatus).valueSeq(),
  );
}

export function selectOemPlanChangeRequestEntitiesByRequestedAssetStatus(
  requestedAssetStatus: OemAssetStatus,
) {
  return createSelector(selectAllOemPlanChangeRequestEntities, (entities) =>
    entities.filter((e) => e.requestedAssetStatus === requestedAssetStatus).valueSeq(),
  );
}

export function selectOemPlanChangeRequestEntitiesByRequestedById(requestedById: string) {
  return createSelector(selectAllOemPlanChangeRequestEntities, (entities) =>
    entities.filter((e) => e.requestedById === requestedById).valueSeq(),
  );
}

export function selectOemPlanChangeRequestEntitiesByDateRequested(dateRequested: Date) {
  return createSelector(selectAllOemPlanChangeRequestEntities, (entities) =>
    entities.filter((e) => e.dateRequested === dateRequested).valueSeq(),
  );
}

export function selectOemPlanChangeRequestEntitiesByDateCompleted(dateCompleted: Date) {
  return createSelector(selectAllOemPlanChangeRequestEntities, (entities) =>
    entities.filter((e) => e.dateCompleted === dateCompleted).valueSeq(),
  );
}

export function selectOemPlanChangeRequestEntitiesByDateCancelled(dateCancelled: Date) {
  return createSelector(selectAllOemPlanChangeRequestEntities, (entities) =>
    entities.filter((e) => e.dateCancelled === dateCancelled).valueSeq(),
  );
}

export function selectOemPlanChangeRequestEntitiesByNotes(notes: string) {
  return createSelector(selectAllOemPlanChangeRequestEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}
//#endregion Selectors
